import { createElement } from 'react';
import { Icon } from '../Icon';
import { AnimatedProgressIndicator } from '@sas/react-svg';
import { Hidden } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../theme';
import { GlobalSettingsKeys, useGetGlobalSetting } from '../Settings';
import bundle from './NovaComponents-Indicator-gui-icu.properties';
import { BaseIndicatorProps, HiddenIndicatorProps, IndicatorProps } from './types';

import cx from 'classnames';
import cn from './Indicator.css.json';
import './Indicator.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

const BaseIndicator = (props: BaseIndicatorProps) => {
  return (
    <span
      aria-hidden="true"
      className={cx(cn.container, props.className)}
      data-testid={props.testId}
      style={props.style}
    >
      {props.content}
    </span>
  );
};

export const Indicator = ({ className, count, testId, type, ...props }: IndicatorProps) => {
  const playVisualEffects = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars(getIndicatorTokens(dt, playVisualEffects), 'indicator'),
  };
  const style = { ...tokens, ...props.style };
  const localize = useTextLocalizer();

  switch (type) {
    case 'processing':
      return (
        <BaseIndicator
          className={className}
          content={
            <Icon
              className={cn.processing}
              icon={AnimatedProgressIndicator}
              tooltip={localize('processing.label')}
            />
          }
          style={style}
          testId={testId}
        />
      );
    case 'alert':
      return (
        <BaseIndicator
          className={cx(cn.alert, className)}
          content="!"
          style={style}
          testId={testId}
        />
      );
    case 'unsaved':
      return <BaseIndicator className={className} content="*" style={style} testId={testId} />;
    case 'readOnly':
      return (
        <BaseIndicator
          className={className}
          content={localize('readOnly.label')}
          style={style}
          testId={testId}
        />
      );
    case 'hidden':
      return (
        <BaseIndicator
          className={className}
          content={
            <Icon
              icon={Hidden}
              tooltip={(props as HiddenIndicatorProps).tooltip ?? localize('hidden.label')}
            />
          }
          style={style}
          testId={testId}
        />
      );
    case 'ready':
      return (
        <BaseIndicator
          className={cx(cn.ready, className)}
          content="•"
          style={style}
          testId={testId}
        />
      );
    case 'count':
      return count !== undefined ? (
        <BaseIndicator className={className} content={`(${count})`} style={style} testId={testId} />
      ) : null;
  }
};

const getIndicatorTokens = (dt: DesignTokens, playVisualEffects: boolean) => {
  return {
    'processing-anim-duration': dt.animation.duration.spin,
    'processing-anim-iteration-count': playVisualEffects ? 'infinite' : 0,
    'alert-fg': dt.c.colorAlert,
    'ready-fg': dt.c.colorInfo,
    fg: dt.c.colorTextDefault,
    'font-size-m': dt.font.size.m,
  };
};
